import { useState, useEffect, useRef, useCallback } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ProductCard from "./Card";
import productAPI from "../../api/productApi";
import FilterSidebar from "./detailfeatures/Filtersidebar";
import { normalizeProduct } from "../../utils/normalizeProduct";
import Breadcrump from "../../components/Breadcrump";

gsap.registerPlugin(ScrollTrigger);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const ChevronLeft = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 13 13"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M8 2L3 6.5 8 11" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronRight = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 13 13"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M5 2l5 4.5L5 11" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const FilterIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 13 13"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M1 2h11M3 6.5h7M5 11h3" strokeLinecap="round" />
  </svg>
);
const CloseIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M1 1l8 8M9 1L1 9" strokeLinecap="round" />
  </svg>
);

// ─── Sort Options (toolbar dropdown ke liye — same as ListPage) ───────────────
// NOTE: agar API filterData.sorting bhejti hai toh woh FilterSidebar mein dikhega.
//       Yeh SORT_OPTIONS sirf toolbar select ke liye hain.
const SORT_OPTIONS = [
  { value: "", label: "Default" },
  { value: "newest", label: "Newest First" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_dsc", label: "Price: High to Low" },
  { value: "popular", label: "Most Popular" },
];

const PRODUCTS_PER_PAGE = 8; // server ko bhi yahi bhejna

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Collection() {
  // ── API State
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterData, setFilterData] = useState({});

  // ── Filter / Sort / Page State
  const [filters, setFilters] = useState({
    category: [], // array of _ids
    brand: [], // array of ids
    min: "",
    max: "",
    sort: "",
  });
  const [priceRange, setPriceRange] = useState([100, 10100]);
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // ── Debounce ref for price slider
  const debounceRef = useRef(null);

  // ── GSAP Refs
  const eyebrowRef = useRef(null);
  const headingRef = useRef(null);
  const descRef = useRef(null);
  const toolbarRef = useRef(null);
  const sidebarRef = useRef(null);
  const gridRef = useRef(null);

  // ─── Fetch Products ───────────────────────────────────────────────────────
  const fetchProducts = useCallback(async (page, appliedFilters) => {
    setLoading(true);
    try {
      const res = await productAPI.listing({
        page,
        limit: PRODUCTS_PER_PAGE,
        ...appliedFilters,
      });

      if (res.data) {
        const { products, pagination } = res.data;
        setItems((products || []).map(normalizeProduct));
        setTotalCount(pagination?.totalCount || 0);
      }
    } catch (err) {
      console.error("Error loading products:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Fetch Filter Data (categories, brands, priceRange, sorting) ──────────
  const fetchFilters = useCallback(async () => {
    try {
      const res = await productAPI.filters();
      if (res.data) {
        setFilterData(res.data);
        if (res.data?.priceRange) {
          setPriceRange([res.data.priceRange.min, res.data.priceRange.max]);
        }
      }
    } catch (err) {
      console.error("Filter API error:", err);
    }
  }, []);

  // ─── On mount → filters fetch
  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  // ─── On page/filters change → products fetch
  useEffect(() => {
    fetchProducts(currentPage, filters);
  }, [currentPage, filters, fetchProducts]);

  // ─── GSAP entrance animation (runs once)
  useEffect(() => {
    if (typeof gsap === "undefined") return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: eyebrowRef.current,
          start: "top 85%",
          once: true,
        },
        defaults: { ease: "power3.out" },
      });

      tl.to(eyebrowRef.current, { opacity: 1, duration: 0.65 }, 0)
        .to(headingRef.current, { opacity: 1, y: 0, duration: 0.85 }, 0.18)
        .to(descRef.current, { opacity: 1, duration: 0.65 }, 0.42)
        .to(toolbarRef.current, { opacity: 1, duration: 0.55 }, 0.55)
        .to(sidebarRef.current, { opacity: 1, duration: 0.55 }, 0.65);
    });

    return () => ctx.revert();
  }, []);

  // ─── GSAP card animation on items change
  useEffect(() => {
    if (!gridRef.current || items.length === 0) return;
    const cards = gridRef.current.querySelectorAll(".pcard");
    gsap.fromTo(
      cards,
      { opacity: 0, y: 18 },
      {
        opacity: 1,
        y: 0,
        duration: 0.5,
        stagger: 0.07,
        ease: "power3.out",
        overwrite: true,
      },
    );
  }, [items]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  // Checkbox — category / brand
  const handleCheckboxChange = useCallback((key, value) => {
    setCurrentPage(1);
    setFilters((prev) => {
      const current = prev[key] || [];
      const exists = current.includes(value);
      return {
        ...prev,
        [key]: exists
          ? current.filter((x) => x !== value)
          : [...current, value],
      };
    });
  }, []);

  // Single value — sort
  const handleFilterChange = useCallback((key, value) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Price slider — debounced
  const handlePriceChange = useCallback((range) => {
    setPriceRange(range); // immediate UI update

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      setFilters((prev) => ({
        ...prev,
        min: range[0],
        max: range[1],
      }));
    }, 500);
  }, []);

  // Sort from toolbar dropdown
  const handleSortChange = useCallback((value) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, sort: value }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setCurrentPage(1);
    setFilters({ category: [], brand: [], min: "", max: "", sort: "" });
    // price range bhi reset — filterData se
    if (filterData?.priceRange) {
      setPriceRange([filterData.priceRange.min, filterData.priceRange.max]);
    }
  }, [filterData]);

  // ─── Active filter tags (for pill display under toolbar) ──────────────────
  const activeFilters = (() => {
    const tags = [];

    filters.category.forEach((id) => {
      const match = filterData?.categories?.find((c) => c._id === id);
      if (match)
        tags.push({
          label: match.name,
          key: `category-${id}`,
          type: "category",
          val: id,
        });
    });

    filters.brand.forEach((id) => {
      const match = filterData?.brands?.find((b) => b.id === id);
      if (match)
        tags.push({
          label: match.name,
          key: `brand-${id}`,
          type: "brand",
          val: id,
        });
    });

    if (filters.sort) {
      const match = SORT_OPTIONS.find((o) => o.value === filters.sort);
      if (match) tags.push({ label: match.label, key: "sort", type: "sort" });
    }

    if (filters.min !== "" || filters.max !== "") {
      tags.push({
        label: `₹${Number(filters.min).toLocaleString("en-IN")} – ₹${Number(filters.max).toLocaleString("en-IN")}`,
        key: "price",
        type: "price",
      });
    }

    return tags;
  })();

  const removeTag = useCallback(
    (tag) => {
      setCurrentPage(1);
      if (tag.type === "category") {
        setFilters((f) => ({
          ...f,
          category: f.category.filter((x) => x !== tag.val),
        }));
      } else if (tag.type === "brand") {
        setFilters((f) => ({
          ...f,
          brand: f.brand.filter((x) => x !== tag.val),
        }));
      } else if (tag.type === "sort") {
        setFilters((f) => ({ ...f, sort: "" }));
      } else if (tag.type === "price") {
        setFilters((f) => ({ ...f, min: "", max: "" }));
        if (filterData?.priceRange) {
          setPriceRange([filterData.priceRange.min, filterData.priceRange.max]);
        }
      }
    },
    [filterData],
  );

  // ─── Pagination helpers ───────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(totalCount / PRODUCTS_PER_PAGE));

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("…");
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push("…");
      pages.push(totalPages);
    }
    return pages;
  };

  // ─── JSX ──────────────────────────────────────────────────────────────────
  return (
    <>
      <Breadcrump title="Collection" />
      <section className="shop-page" aria-labelledby="shop-heading">
        <div className="container">
          {/* ── Page Header ── */}
          {/* <div className="row align-items-end shop-header">
            <div className="col-12 col-md-8">
              <p className="shop-eyebrow" ref={eyebrowRef}>
                <span className="shop-eyebrow-line" aria-hidden="true" />
                Our Collection
              </p>
              <h1 className="shop-heading" id="shop-heading" ref={headingRef}>
                Discover <em>Timeless</em>
                <br className="d-none d-md-block" /> Ethnic Fashion
              </h1>
              <p className="shop-description" ref={descRef}>
                Browse our curated range of handcrafted ethnic pieces designed
                with heritage craftsmanship.
              </p>
            </div>
            <div className="col-12 col-md-4 d-flex justify-content-md-end align-items-end mt-2 mt-md-0">
              <span className="shop-result-count">
                {totalCount} piece{totalCount !== 1 ? "s" : ""}
              </span>
            </div>
          </div> */}

          {/* ── Toolbar ── */}
          <div className="shop-toolbar" ref={toolbarRef}>
            {/* Mobile filter toggle */}
            <div
              className="shop-filter-chips"
              role="group"
              aria-label="Filter controls"
            >
              <button
                type="button"
                className="shop-mobile-filter-btn"
                onClick={() => setMobileFiltersOpen((x) => !x)}
                aria-expanded={mobileFiltersOpen}
              >
                <FilterIcon />
                Filters
              </button>
            </div>

            {/* Sort (toolbar dropdown) */}
            <div className="shop-sort-wrap">
              <span className="shop-sort-label" id="sort-label">
                Sort
              </span>
              <select
                className="shop-sort-select"
                value={filters.sort}
                onChange={(e) => handleSortChange(e.target.value)}
                aria-labelledby="sort-label"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Active filter tags ── */}
          {activeFilters.length > 0 && (
            <div
              className="shop-active-filters"
              role="list"
              aria-label="Active filters"
            >
              {activeFilters.map((tag) => (
                <button
                  key={tag.key}
                  type="button"
                  className="shop-active-tag"
                  onClick={() => removeTag(tag)}
                  aria-label={`Remove filter: ${tag.label}`}
                  role="listitem"
                >
                  {tag.label}
                  <span className="shop-active-tag-x" aria-hidden="true">
                    <CloseIcon />
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* ── Mobile filter drawer ── */}
          <div
            className={`shop-mobile-drawer${mobileFiltersOpen ? " open" : ""}`}
            aria-hidden={!mobileFiltersOpen}
          >
            <FilterSidebar
              filterData={filterData}
              filters={filters}
              onFilterChange={handleFilterChange}
              onCheckboxChange={handleCheckboxChange}
              priceRange={priceRange}
              onPriceChange={handlePriceChange}
              onClear={clearFilters}
            />
          </div>

          {/* ── Sidebar + Grid ── */}
          <div className="shop-layout">
            {/* Desktop Sidebar */}
            <div ref={sidebarRef}>
              <FilterSidebar
                filterData={filterData}
                filters={filters}
                onFilterChange={handleFilterChange}
                onCheckboxChange={handleCheckboxChange}
                priceRange={priceRange}
                onPriceChange={handlePriceChange}
                onClear={clearFilters}
              />
            </div>

            {/* Product Grid */}
            <div>
              <div
                className="shop-grid"
                ref={gridRef}
                aria-live="polite"
                aria-label="Product listing"
              >
                {loading ? (
                  <div className="shop-loading">Loading…</div>
                ) : items.length === 0 ? (
                  <div className="shop-empty">
                    <span className="shop-empty-icon" aria-hidden="true">
                      ✦
                    </span>
                    <p className="shop-empty-text">
                      No pieces match your current filters.
                      <br />
                      Try adjusting your selection.
                    </p>
                  </div>
                ) : (
                  items.map((product) => (
                    <ProductCard
                      key={product._id || product.id}
                      product={product}
                    />
                  ))
                )}
              </div>

              {/* ── Pagination ── */}
              {!loading && totalPages > 1 && (
                <nav className="shop-pagination" aria-label="Product pages">
                  <button
                    className="shop-page-btn"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <ChevronLeft />
                  </button>

                  {getPageNumbers().map((page, i) =>
                    page === "…" ? (
                      <span
                        key={`ellipsis-${i}`}
                        className="shop-page-ellipsis"
                        aria-hidden="true"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        className={`shop-page-btn${currentPage === page ? " active" : ""}`}
                        onClick={() => setCurrentPage(page)}
                        aria-label={`Page ${page}`}
                        aria-current={currentPage === page ? "page" : undefined}
                      >
                        {page}
                      </button>
                    ),
                  )}

                  <button
                    className="shop-page-btn"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    <ChevronRight />
                  </button>
                </nav>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
